import { Inject, Injectable } from '@angular/core';
import { platformRoutePath } from '../constants-types';
import { CURRENT_APP_TYPE } from '../core-data-model';
import { AppType } from '../shared';
import {
    LegacyModuleAccessInitializedMessage,
    Logger,
    MessagingService
} from '../system-services';

export type LegacyModuleResponse = {
    isLegacy: boolean;
    isEnabled: boolean;
};
@Injectable({ providedIn: 'root' })
export class PlatformBootstrapHelperService {
    hasNavigated = false;
    private accountManagementApiPrefix = '/api/v1';

    constructor(
        private logger: Logger,
        private messagingService: MessagingService,
        @Inject(CURRENT_APP_TYPE) public currentAppType: AppType
    ) {}

    async getLegacyModuleAccessCheck(
        token: string,
        companyId?: string
    ): Promise<LegacyModuleResponse> {
        const url = this.constructUrl(companyId);
        try {
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            if (!response.ok) {
                // Check for response ok and if it fails we set up a default value of Feature flag false and continue
                this.logError(response, companyId);
                return { isLegacy: true, isEnabled: false };
            }
            return await response.json();
        } catch (error) {
            this.logError(error, companyId);
            return { isLegacy: true, isEnabled: false };
        }
    }

    /*
     When a user logs in, we will check if they have access to any 1.0 modules and if they do then we will navigate them to SPA app.
    If they don't then they'll be navigated to the platform app. We have AFD in place to proxy to Platform app when it sees /pi/.
    When the url has platform in it, but they have access to 1.0 module then we'll strip the /pi/ string and navigate to SPA.
    */

    async checkLegacyModuleAccessAndNavigate(
        authToken: string,
        companyId?: string,
        deepLinkState?: string
    ) {
        // Allow tests to bypass localhost environment short-circuit by setting a flag.
        if (
            window.location.host.includes('localhost') &&
            !(window as any).__BYPASS_LOCALHOST_CHECK__
        ) {
            this.hasNavigated = true;
            return;
        }

        const legacyModuleResult = await this.getLegacyModuleAccessCheck(
            authToken,
            companyId
        );

        if (legacyModuleResult.isEnabled) {
            // enablePlatform FF is turned on
            if (!legacyModuleResult.isLegacy && this.isInPlatformApp()) {
                // Making sure the account doesn't have access to any 1.0 modules and if we're already in Platform app
                this.hasNavigated = true;
                return;
            }

            if (legacyModuleResult.isLegacy) {
                // The account has access to 1.0 module remove /pi and navigate
                this.removePlatformAndNavigateToSpa(deepLinkState);
            } else {
                // The account doesn't have access to any 1.0 modules and navigate to Platform
                this.addPlatformAndNavigate(deepLinkState);
            }
        } else {
            // enablePlatform FF is turned off
            if (
                this.isInPlatformAppContext() ||
                this.isInPlatformApp() ||
                deepLinkState?.includes(platformRoutePath)
            ) {
                // The path has /pi, and we'll remove it and navigate to SPA
                this.removePlatformAndNavigateToSpa(deepLinkState);
            }
            // The path is clear of /pi, and we can continue with bootstrapping
            else {
                this.hasNavigated = true;
            }
        }

        this.messagingService.sendMessage(
            new LegacyModuleAccessInitializedMessage()
        );
    }

    private removePlatformAndNavigateToSpa(deepLinkState: string) {
        if (
            this.isInPlatformAppContext() ||
            deepLinkState?.includes(platformRoutePath)
        ) {
            // remove /pi from deepLink
            const filteredPath =
                deepLinkState && deepLinkState !== ''
                    ? deepLinkState.replace(`/${platformRoutePath}`, '')
                    : window.location.pathname.replace(
                          `/${platformRoutePath}`,
                          ''
                      );
            const targetUrl = this.buildSpaTargetUrl(filteredPath);
            const navigatorFn = (window as any).__NAVIGATE__;
            if (typeof navigatorFn === 'function') {
                navigatorFn(targetUrl);
            } else {
                window.location.assign(targetUrl);
            }
        } else {
            this.hasNavigated = true;
        }
    }

    private isInPlatformAppContext() {
        return (
            window.location.host.includes(`/${platformRoutePath}/`) ||
            window.location.pathname.includes(`/${platformRoutePath}/`)
        );
    }

    private isInPlatformApp() {
        return this.currentAppType === AppType.Platform;
    }

    private addPlatformAndNavigate(deepLinkState: string) {
        // add /pi/{deep link path} and navigate to Platform app

        /**
         * For deep links, Auth0 will encode the deep links before logging in, we need to extract that before we navigate to Platform app.
         * When the user is not authenticated we can fetch that from Auth0
         * Or
         * If the user is already authenticated we can fetch that from location.path
         * **/
        const extractedDeepLinkPath =
            deepLinkState && deepLinkState !== ''
                ? deepLinkState
                : window.location.pathname;

        // If deeplink already contains Platform then no need to prefix it
        const path = extractedDeepLinkPath.includes(platformRoutePath)
            ? extractedDeepLinkPath
            : `/${platformRoutePath}${extractedDeepLinkPath}`;

        // You cannot use window.replace as it breaks the browser history used to populate the referer header,
        // which is in turn used to in AFD to decide which environment to send the initdata request to
        const targetUrl = this.buildSpaTargetUrl(path);
        const navigatorFn = (window as any).__NAVIGATE__;
        if (typeof navigatorFn === 'function') {
            navigatorFn(targetUrl);
        } else {
            window.location.assign(targetUrl);
        }
    }

    /**
     * Safely builds a redirect URL on the trusted SPA origin from a
     * caller-supplied path segment. The target always starts from the trusted
     * spaAppUrl; only the path, query and fragment of the resolved segment are
     * copied onto it, and only when the segment resolves to the same origin.
     * This prevents open-redirect breakout payloads (e.g. a leading
     * "@evil.com", "//evil.com", "\\evil.com", or an absolute
     * "https://evil.com") from changing the destination host.
     */
    private buildSpaTargetUrl(pathSegment: string): string {
        const spaAppUrl = (window as any).PI.appSettings.spaAppUrl;
        try {
            const target = new URL(spaAppUrl);
            const resolved = new URL(pathSegment ?? '', target);
            if (resolved.origin === target.origin) {
                target.pathname = resolved.pathname;
                target.search = resolved.search;
                target.hash = resolved.hash;
            }
            return target.href;
        } catch {
            return spaAppUrl;
        }
    }

    private logError(response: Response, companyId: string) {
        this.logger.logError(
            'Error occurred during legacy module check. Error: status' +
                response.status +
                'Message' +
                response.statusText,
            companyId ? companyId : 'No company Id'
        );
    }

    private constructUrl(companyId) {
        return companyId
            ? `${(window as any).PI.appSettings.accountManagementApiUrl}${
                  this.accountManagementApiPrefix
              }/companies/${companyId}/legacymodule`
            : `${(window as any).PI.appSettings.accountManagementApiUrl}${
                  this.accountManagementApiPrefix
              }/companies/legacymodule`;
    }
}
